/**
 * NOTE [PRUNE_CANDIDATE]: Used by GlobalRemy and RecommendationsFusion.
 * If those call sites are retired, this component can be removed.
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ShoppingBag, MapPin, 
  Sparkles, Trash2, Share2
} from 'lucide-react';
import { toast } from '@/lib/utils/toast';
import { CollectionShareModal } from '@/components/collections/CollectionShareModal';
import { ShortlistSuggestions } from '@/components/collections/ShortlistSuggestions';

interface ActiveShortlistProps {
  isOpen: boolean;
  onClose: () => void;
  items: any[];
  onRemoveItem: (id: string) => void;
  city: string;
  remyEnabled?: boolean;
  trustLevel?: 'observing' | 'suggesting' | 'planning' | 'delegating';
  onTrustIncrease?: () => void;
}

export function ActiveShortlist({
  isOpen,
  onClose,
  items,
  onRemoveItem,
  city,
  remyEnabled = false,
  trustLevel = 'observing',
  onTrustIncrease
}: ActiveShortlistProps) {
  const [notes, setNotes] = useState('');
  const [shortlistName, setShortlistName] = useState('');
  const [shortlistTheme, setShortlistTheme] = useState('');
  const [_showRemyPlan, setShowRemyPlan] = useState(false);
  const [optimizedOrder, setOptimizedOrder] = useState<any[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  
  // Safe text helpers for fields that might be string | string[] | object | null
  const normalizeToLower = (value: any): string => {
    if (value == null) return '';
    if (typeof value === 'string') return value.toLowerCase();
    if (Array.isArray(value)) {
      return value
        .map(v => (typeof v === 'string' ? v : String(v)))
        .join(' ')
        .toLowerCase();
    }
    if (typeof value === 'object') {
      try {
        return Object.values(value)
          .map(v => (typeof v === 'string' ? v : String(v)))
          .join(' ')
          .toLowerCase();
      } catch {
        return '';
      }
    }
    return String(value).toLowerCase();
  };
  const lowerIncludes = (value: any, needle: string): boolean => normalizeToLower(value).includes(needle);
  
  // Remove explicit time/distance emphasis; focus on analytical insights
  
  const getRemyAnalysis = () => {
    if (!remyEnabled) return null;
    
    const analysis: {
      gaps: string[];
      optimizations: { type: string; message: string; action: string }[];
      suggestions: { message: string; confidence: number }[];
    } = {
      gaps: [],
      optimizations: [],
      suggestions: []
    };
    
    if (items.length > 0) {
      const _vibes = items.flatMap(i => i.vibes || (i.vibe ? [i.vibe] : [])).filter(Boolean);
      const _cuisineTypes = items.map(i => i.cuisine_type).filter(Boolean);
      const pricePoints = items.map(i => i.price_level).filter(Boolean);
      const avgPrice = pricePoints.length > 0 
        ? pricePoints.reduce((a: number, b: number) => a+b, 0) / pricePoints.length 
        : 0;
      
      const hasUpscale = items.some(i => 
        (i.vibes?.some((v: string) => v.toLowerCase().includes('sophisticated') || v.toLowerCase().includes('upscale'))) ||
        (i.vibe?.toLowerCase().includes('sophisticated') || i.vibe?.toLowerCase().includes('upscale')) ||
        i.price_level >= 4
      );
      const hasLocal = items.some(i => 
        (i.vibes?.some((v: string) => v.toLowerCase().includes('local') || v.toLowerCase().includes('authentic'))) ||
        (i.vibe?.toLowerCase().includes('local') || i.vibe?.toLowerCase().includes('authentic')) ||
        (i.insider_tips?.some((t: string) => t.toLowerCase().includes('local'))) ||
        (i.insider_tip?.toLowerCase().includes('local'))
      );
      const _hasTrendy = items.some(i => 
        (i.vibes?.some((v: string) => v.toLowerCase().includes('trendy') || v.toLowerCase().includes('instagram'))) ||
        (i.vibe?.toLowerCase().includes('trendy') || i.vibe?.toLowerCase().includes('instagram')) ||
        lowerIncludes(i.best_for, 'scene')
      );
      
      if (hasUpscale && !items.some(i => i.category?.includes('bar'))) {
        analysis.gaps.push('You\'re going upscale but missing cocktails - that\'s half the experience');
      }
      
      if (hasLocal && items.length >= 3 && !items.some(i => 
        i.cuisine_type?.toLowerCase().includes('portuguese') || 
        i.cuisine_type?.toLowerCase().includes('traditional')
      )) {
        analysis.gaps.push('Going for authentic but no traditional Portuguese spot? That\'s the soul of Lisbon');
      }
      
      if (avgPrice >= 3 && !items.some(i => i.price_level <= 2)) {
        analysis.gaps.push('All high-end choices - consider a casual spot for balance (and your wallet)');
      }
      
      const hasMorning = items.some(i => 
        lowerIncludes(i.best_for, 'breakfast') ||
        lowerIncludes(i.best_for, 'brunch') ||
        i.category?.toLowerCase().includes('cafe')
      );
      const _hasAfternoon = items.some(i => 
        lowerIncludes(i.best_for, 'lunch') ||
        lowerIncludes(i.best_for, 'afternoon')
      );
      const hasEvening = items.some(i => 
        lowerIncludes(i.best_for, 'dinner') ||
        lowerIncludes(i.best_for, 'drinks') ||
        i.category?.toLowerCase().includes('bar')
      );
      
      if (items.length >= 4 && !hasMorning) {
        analysis.gaps.push('No morning spot - where\'s the coffee to start this adventure?');
      }
      
      if (items.length >= 3 && !hasEvening) {
        analysis.gaps.push('Ends too early - add a dinner or drinks spot to complete the day');
      }
    }
    
    if (items.length >= 2) {
      const neighborhoods = items.map(i => i.detected_neighborhood || i.neighborhood).filter(Boolean);
      const uniqueNeighborhoods = [...new Set(neighborhoods)];
      
      if (uniqueNeighborhoods.length > 1) {
        analysis.optimizations.push({
          type: 'sequence',
          message: `Spans ${uniqueNeighborhoods.length} neighborhoods - ${uniqueNeighborhoods.slice(0, 2).join(' & ')}`,
          action: 'view map'
        });
      }
    }
    
    if (items.length >= 2 && (trustLevel === 'suggesting' || trustLevel === 'planning' || trustLevel === 'delegating')) {
      const neighborhoods = items.map(i => i.detected_neighborhood || i.neighborhood).filter(Boolean);
      const uniqueNeighborhoods = [...new Set(neighborhoods)];
      
      const vibeProfile = items.flatMap(i => i.vibes || (i.vibe ? [i.vibe] : [])).join(' ').toLowerCase();
      const cuisineProfile = items.map(i => i.cuisine_type?.toLowerCase() || '').join(' ');
      
      if (vibeProfile.includes('trendy') && vibeProfile.includes('local')) {
        analysis.suggestions.push({
          message: 'You\'re mixing trendy with local - that\'s the perfect Lisbon formula. Add Time Out Market to bridge both worlds',
          confidence: 0.85
        });
      }
      
      if (cuisineProfile.includes('wine') && !cuisineProfile.includes('cheese')) {
        analysis.suggestions.push({
          message: 'Wine spots but no cheese? Queijaria in Campo de Ourique would complete this perfectly',
          confidence: 0.8
        });
      }
      
      if (items.every(i => i.price_level >= 3)) {
        analysis.suggestions.push({
          message: 'Going all out! Consider Belcanto for the ultimate splurge to crown this luxury tour',
          confidence: 0.75
        });
      }
      
      if (uniqueNeighborhoods.includes('Bairro Alto') && !items.some(i => i.category?.includes('bar'))) {
        analysis.suggestions.push({
          message: 'In Bairro Alto without a fado bar? That\'s like Paris without wine - add Tasca do Chico',
          confidence: 0.9
        });
      }
      
      const hasInsiderSpots = items.filter(i => 
        i.friend_count > 0 || 
        (i.insider_tips?.some((t: string) => t.toLowerCase().includes('local'))) ||
        (i.insider_tip?.toLowerCase().includes('local')) ||
        (i.vibes?.some((v: string) => v.toLowerCase().includes('hidden'))) ||
        (i.vibe?.toLowerCase().includes('hidden'))
      ).length >= 2;
      
      if (hasInsiderSpots) {
        analysis.suggestions.push({
          message: 'You\'ve got the insider track - these aren\'t tourist traps. Your friends know what\'s up',
          confidence: 0.95
        });
      }
      
      const totalFriendRecs = items.reduce((sum, i) => sum + (i.friend_count || 0), 0);
      if (totalFriendRecs >= 5) {
        analysis.suggestions.push({
          message: `${totalFriendRecs} friend recommendations here - this is basically your crew's official ${city} guide`,
          confidence: 1.0
        });
      }
    }
    
    return analysis;
  };
  
  const analysis = getRemyAnalysis();
  const visibleItems = (optimizedOrder.length > 0 ? optimizedOrder : items);
  const listItems = showAll ? visibleItems : visibleItems.slice(0, 5);
  
  const handleOptimizeRoute = () => {
    // Light sequencing by neighborhood
    const optimized = [...items].sort((a, b) => {
      // Sort by neighborhood/vicinity for now
      return (a.vicinity || '').localeCompare(b.vicinity || '');
    });
    setOptimizedOrder(optimized);
    setShowRemyPlan(true);
    toast.success('Sequence refined — grouped by neighborhood');
    
    if (onTrustIncrease) {
      onTrustIncrease();
    }
  };
  
  const handleCreateItinerary = () => {
    if (trustLevel === 'delegating') {
      // Full AI planning
      toast.success('Remy is creating your perfect day...');
      setTimeout(() => {
        toast.success('Itinerary ready! Check your email');
      }, 2000);
    } else {
      setShowRemyPlan(true);
      toast.info('Let\'s plan this together');
    }
  };
  
  const _displayItems = optimizedOrder.length > 0 ? optimizedOrder : items;
  
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-charcoal/50 backdrop-blur-sm z-40"
            onClick={onClose}
          />
          
          {/* Cart Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full md:w-[480px] bg-bone shadow-2xl z-50"
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="p-6 border-b border-sand-new">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <ShoppingBag className="w-6 h-6 text-olive" />
                    <h2 className="font-brand-display text-2xl text-ink">Your {city} shortlist</h2>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-sand-new rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-charcoal" />
                  </button>
                </div>
                
                {/* Taste-centric summary (analytical guidance) */}
                <div className="flex items-center gap-3 text-sm text-ash">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {items.length} saved
                  </span>
                  <span className="text-ash/80">Remy highlights gaps and smart sequencing.</span>
                </div>
              </div>
              
              {/* Remy Guidance */}
              {remyEnabled && analysis && (analysis.gaps.length > 0 || analysis.optimizations.length > 0) && (
                <div className="p-4 bg-olive/5 border-b border-olive/20">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-olive" />
                    <span className="text-sm font-body text-charcoal font-medium">
                      Remy's Guidance
                    </span>
                  </div>
                  
                  {/* Gaps */}
                  {analysis.gaps.length > 0 && (
                    <div className="mb-3">
                      {analysis.gaps.map((gap, i) => (
                        <p key={i} className="text-sm text-charcoal mb-1">
                          ⚠️ {gap}
                        </p>
                      ))}
                    </div>
                  )}
                  
                  {/* Helpful nudges (not route optimization language) */}
                  {analysis.optimizations.map((opt, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <p className="text-sm text-charcoal">💡 {opt.message}</p>
                      <button
                        onClick={handleOptimizeRoute}
                        className="px-3 py-1 bg-olive text-bone rounded-full text-xs
                                 font-body uppercase tracking-wider hover:bg-rust transition-colors"
                      >
                        {opt.action}
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Items List */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Shortlist naming prompt after 3+ items */}
                {items.length >= 3 && (
                  <div className="mb-4 p-3 bg-earth-800 border border-sand-new/40 rounded-lg">
                    <div className="text-xs text-sand-new mb-2">Name this shortlist (optional)</div>
                    <div className="flex gap-2">
                      <input
                        value={shortlistName}
                        onChange={(e) => setShortlistName(e.target.value)}
                        placeholder="e.g., Pizza Night, Best Cocktails"
                        className="flex-1 px-3 py-2 bg-earth-900 border border-sand-new/60 rounded-lg text-sm text-bone placeholder-sand-new focus:outline-none focus:ring-1 focus:ring-olive"
                      />
                      <input
                        value={shortlistTheme}
                        onChange={(e) => setShortlistTheme(e.target.value)}
                        placeholder="Theme (optional)"
                        className="w-40 px-3 py-2 bg-earth-900 border border-sand-new/60 rounded-lg text-sm text-bone placeholder-sand-new focus:outline-none focus:ring-1 focus:ring-olive"
                      />
                    </div>
                  </div>
                )}
                {items.length === 0 ? (
                  <div className="text-center py-12">
                    <ShoppingBag className="w-12 h-12 text-ash/30 mx-auto mb-4" />
                    <p className="text-ash mb-2">No spots planned yet</p>
                    <p className="text-sm text-ash/70">
                      Add spots from the city guide to start planning
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {listItems.map((item, index) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-parchment rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs text-ash">
                                {index + 1}
                              </span>
                              <h4 className="font-brand-display text-ink">
                                {item.place_name}
                              </h4>
                            </div>
                            <p className="text-sm text-ash mb-1">
                              {item.category} • {item.vicinity}
                            </p>
                            {/* Social proof merged */}
                            {(item.recommender_name || (item.additional_recommenders && item.additional_recommenders.length > 0)) && (
                              <p className="text-xs text-ash">
                                Recommended by {item.recommender_name || 'curators'}
                                {item.additional_recommenders && item.additional_recommenders.length > 0 ? ` + ${item.additional_recommenders.length}` : ''}
                              </p>
                            )}
                            {/* Quick links */}
                            <div className="mt-2 flex items-center gap-3 text-xs">
                              {(() => {
                                const maps = item.google_maps_link || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${item.place_name || ''} ${city}`)}`;
                                return (
                                  <a href={maps} target="_blank" rel="noopener noreferrer" className="text-olive hover:text-rust">
                                    Map
                                  </a>
                                );
                              })()}
                              {item.phone && (
                                <a href={`tel:${item.phone}`} className="text-olive hover:text-rust">
                                  Call
                                </a>
                              )}
                              {item.website && (
                                <a href={item.website} target="_blank" rel="noopener noreferrer" className="text-olive hover:text-rust">
                                  Website
                                </a>
                              )}
                            </div>
                            {optimizedOrder.length > 0 && (
                              <p className="text-xs text-olive">↑ Sequenced for smoother flow</p>
                            )}
                          </div>
                          <button
                            onClick={() => onRemoveItem(item.id)}
                            className="p-2 hover:bg-rust/10 rounded transition-colors"
                          >
                            <Trash2 className="w-4 h-4 text-ash hover:text-rust" />
                          </button>
                        </div>
                      </motion.div>
                    ))}
                    {/* Show more / less */}
                    {visibleItems.length > 5 && (
                      <div className="pt-1">
                        <button
                          onClick={() => setShowAll(v => !v)}
                          className="w-full py-2 bg-bone border border-sand-new rounded-lg text-sm hover:bg-sand-new"
                        >
                          {showAll ? 'Show less' : `Show ${visibleItems.length - 5} more`}
                        </button>
                      </div>
                    )}
                    
                    {/* Remy Suggestions */}
                    {analysis?.suggestions.map((suggestion, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-olive/5 border border-olive/20 rounded-lg p-4"
                      >
                        <div className="flex items-start gap-3">
                          <Sparkles className="w-4 h-4 text-olive mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm text-charcoal">
                              {suggestion.message}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <button className="text-xs text-olive hover:text-rust">
                                Add to plan
                              </button>
                              <span className="text-xs text-ash">•</span>
                              <button className="text-xs text-ash hover:text-charcoal">
                                Not interested
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}

                    {/* Thematic shortlist suggestions via LLM - only after 3 items */}
                    {items.length >= 3 && (
                      <div className="pt-3">
                        <div className="text-xs text-ash mb-2">Thematic shortlist ideas</div>
                        <ShortlistSuggestions city={city} />
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Footer Actions */}
              {items.length > 0 && (
                <div className="p-6 border-t border-sand-new bg-parchment">
                  {/* Notes */}
                  <div className="mb-4">
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add notes for your trip..."
                      className="w-full p-3 bg-bone rounded-lg text-sm text-charcoal
                               placeholder-ash resize-none focus:outline-none focus:ring-2
                               focus:ring-olive"
                      rows={2}
                    />
                  </div>
                  
                  {/* Actions */}
                  <div className="space-y-2">
                    {remyEnabled && trustLevel !== 'observing' && (
                      <button
                        onClick={handleCreateItinerary}
                        className="w-full py-3 bg-olive text-bone rounded-lg font-body
                                 uppercase tracking-wider hover:bg-rust transition-colors
                                 flex items-center justify-center gap-2"
                      >
                        <Sparkles className="w-4 h-4" />
                        {trustLevel === 'delegating' 
                          ? 'Let Remy refine everything'
                          : 'Refine with Remy'}
                      </button>
                    )}
                    
                    <button
                      onClick={() => setShowShareModal(true)}
                      className="w-full py-3 bg-sand-new text-charcoal rounded-lg font-body
                               uppercase tracking-wider hover:bg-olive hover:text-bone
                               transition-colors flex items-center justify-center gap-2"
                    >
                      <Share2 className="w-5 h-5" />
                      Share Shortlist
                    </button>
                  </div>
                  
                  {/* Trust Level Indicator */}
                  {remyEnabled && (
                    <div className="mt-4 pt-4 border-t border-sand-new">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-ash">Remy trust level</span>
                        <div className="flex items-center gap-2">
                          <div className="flex gap-1">
                            {['observing', 'suggesting', 'planning', 'delegating'].map((level) => (
                              <div
                                key={level}
                                className={`w-8 h-1 rounded-full ${
                                  ['observing', 'suggesting', 'planning', 'delegating'].indexOf(trustLevel) >= 
                                  ['observing', 'suggesting', 'planning', 'delegating'].indexOf(level)
                                    ? 'bg-olive'
                                    : 'bg-ash/20'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-olive capitalize">{trustLevel}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
          
          {/* Shortlist Share Modal */}
          <CollectionShareModal
            isOpen={showShareModal}
            onClose={() => setShowShareModal(false)}
            items={items}
            destination={city}
            title={shortlistName ? shortlistName : `My ${city} Shortlist`}
          />
        </>
      )}
    </AnimatePresence>
  );
}
